import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  LinearProgress
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CircularProgress from '@mui/material/CircularProgress';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CircleIcon from '@mui/icons-material/Circle';

const SwitchTable = ({
  sortedFilteredSwitches,
  handleRequestSort,
  orderBy,
  order,
  headerToPropertyMap,
  getApiAccessStatus,
  isUpgradeAvailable,
  handleRebootClick,
  handleRowClick,
  selectedSwitchInTable,
  handleUpgradeClick,
  isUpgradeDisabled,
  fetchConfigAndDownload,
  setIsDialogOpen,
  setErrorDialogOpen,
  setSelectedFailedSwitch,
  availableVersions,
  newSwitchLoading,
  get_loggs,
  delete_id,
  updateApi,
  UpdateSwInfo,
  loadingUpdateSwInfo,
  id,
  selectedSwitchNode,
  setSelectedSwitchNode
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [updatingSwitchIp, setUpdatingSwitchIp] = useState(null);

  useEffect(() => {
    if (!loadingUpdateSwInfo) setUpdatingSwitchIp(null);
  }, [loadingUpdateSwInfo]);

  const totalColumns = 9; // NAME, IP, LOCATION, VERSION, MODEL, PLATFORM, STATUS, APIACCESS, Options

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isVersionLessThan = (version, target) => {
    const versionParts = version.slice(3).split('.').map(Number);
    const targetParts = target.split('.').map(Number);

    for (let i = 0; i < versionParts.length; i++) {
      if (versionParts[i] < targetParts[i]) return true;
      if (versionParts[i] > targetParts[i]) return false;
    }
    return false;
  };

  const handleOpenMenu = (event, switchNode) => {
    event.stopPropagation(); // <- viktigt så inte rowClick triggar
    setAnchorEl(event.currentTarget);
    setSelectedSwitchNode(switchNode);
    setOpenMenu(true);
  };

  const handleCloseMenu = () => setOpenMenu(false);

  const handleOpenConfirmDialog = () => {
    setOpenConfirmDialog(true);
    handleCloseMenu();
  };

  const handleCloseConfirmDialog = () => setOpenConfirmDialog(false);

  const paginatedRows = useMemo(
    () => sortedFilteredSwitches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedFilteredSwitches, page, rowsPerPage]
  );

  const isSelectedRow = (switchNode) => {
    // Stöd både gamla (ip) och nya (chassis_id) selection
    return (
      selectedSwitchInTable === switchNode.chassis_id ||
      selectedSwitchInTable === switchNode.ip
    );
  };

  return (
    <>
      <Table
        style={{
          backgroundColor: 'rgba(0,0,0,0.05)',
          width: '100%',
          borderRadius: '10px',
          border: '1px solid black'
        }}
      >
        <TableHead>
          <TableRow>
            {['NAME', 'IP', 'LOCATION', 'VERSION', 'MODEL', 'PLATFORM', 'STATUS', 'APIACCESS', 'Options'].map(
              (header) => (
                <TableCell
                  key={header}
                  onClick={() => handleRequestSort(header)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: 'white',
                    color: 'black',
                    fontWeight: orderBy === headerToPropertyMap[header] ? 'bold' : 'normal',
                    fontSize: '16px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <TableSortLabel
                    active={orderBy === headerToPropertyMap[header]}
                    direction={orderBy === headerToPropertyMap[header] ? order : 'asc'}
                    IconComponent={
                      orderBy === headerToPropertyMap[header]
                        ? order === 'asc'
                          ? ArrowUpwardIcon
                          : ArrowDownwardIcon
                        : null
                    }
                    sx={{
                      color: 'black',
                      '&.Mui-active': { color: 'black' },
                      '& .MuiTableSortLabel-icon': { color: 'black' }
                    }}
                  >
                    {header}
                  </TableSortLabel>
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {paginatedRows.map((switchNode) => {
            const apiAccess = getApiAccessStatus(switchNode.api_access);

            const { available: upgradeAvailable, latestVersion } = isUpgradeAvailable(
              switchNode.version,
              availableVersions
            );

            const versionCheck =
              typeof switchNode.version === 'string' &&
              switchNode.version.startsWith('WC') &&
              isVersionLessThan(switchNode.version, '16.10.0011');

            const showUpgradeIcon =
              upgradeAvailable &&
              latestVersion &&
              switchNode.sw_status === 0 &&
              switchNode.api_access === 1;

            // status text + color
            let statusText = switchNode.statusText;
            let statusColor = 'inherit';
            let isReboot = false;

            switch (switchNode.sw_status) {
              case 1:
                statusText = 'Image Upgrade initiating...';
                break;
              case 2:
                statusText = 'Upgrade in Progress...';
                break;
              case 3:
                if (switchNode.status === 'online') {
                  statusText = 'Reboot';
                  isReboot = true;
                } else {
                  statusText = 'Offline';
                  statusColor = 'red';
                  isReboot = false;
                }
                break;
              case 4:
                statusText = 'Rebooting...';
                break;
              case 10:
                statusText = 'Upgrade failed. Click for info';
                statusColor = 'red';
                break;
              default:
                statusText = switchNode.status === 'online' ? 'Online' : 'Offline';
                statusColor = switchNode.status === 'online' ? 'green' : 'red';
            }

            const rowSelected = isSelectedRow(switchNode);
            const canJumpToGraph = !!switchNode.chassis_id;

            // skeleton row while updating
            if (loadingUpdateSwInfo && updatingSwitchIp === switchNode.ip) {
              return (
                <TableRow key={switchNode.ip}>
                  {Array.from({ length: totalColumns }).map((_, idx) => {
                    // idx: 0 NAME, 1 IP, 2 LOCATION
                    if (idx === 1) {
                      return (
                        <TableCell key={idx} style={{ padding: '6px 16px', textAlign: 'center' }}>
                          {switchNode.ip}
                        </TableCell>
                      );
                    }
                    if (idx === 2) {
                      return (
                        <TableCell key={idx} style={{ padding: '6px 16px', textAlign: 'center' }}>
                          {switchNode.location}
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell key={idx} style={{ padding: '6px 16px' }}>
                        <Skeleton variant="text" width="100%" />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            }

            return (
              <React.Fragment key={switchNode.ip}>
                <TableRow
                  hover
                  selected={rowSelected}
                  onClick={canJumpToGraph ? () => handleRowClick(switchNode.chassis_id) : undefined}
                  style={{
                    cursor: canJumpToGraph ? 'pointer' : 'default',
                    fontWeight: rowSelected ? 'bold' : 'normal',
                    backgroundColor: rowSelected ? 'rgba(25, 118, 210, 0.12)' : 'inherit',
                    transition: 'background-color 0.2s'
                  }}
                >
                  {/* NAME */}
                  <TableCell style={{ textAlign: 'center', fontWeight: rowSelected ? 'bold' : 'normal' }}>
                    {switchNode.name}
                  </TableCell>

                  {/* IP */}
                  <TableCell style={{ textAlign: 'center' }}>{switchNode.ip}</TableCell>

                  {/* LOCATION */}
                  <TableCell style={{ textAlign: 'center' }}>{switchNode.location}</TableCell>

                  {/* VERSION + upgrade icon */}
                  <TableCell style={{ textAlign: 'center', padding: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip
                        placement="top"
                        title={
                          versionCheck ? (
                            <Typography fontSize="12px">
                              Upgrade disable for 2930F &lt; WC.16.10.0011, API issues
                            </Typography>
                          ) : (
                            ''
                          )
                        }
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            color: versionCheck ? 'red' : 'inherit',
                            cursor: versionCheck ? 'not-allowed' : 'default'
                          }}
                        >
                          <span>{switchNode.version}</span>
                          {versionCheck && <InfoOutlinedIcon style={{ marginLeft: '5px' }} />}
                        </div>
                      </Tooltip>

                      {showUpgradeIcon && (
                        <Tooltip
                          placement="top"
                          title={
                            <Typography fontSize="12px">
                              New image version available: {latestVersion.sw_version}
                            </Typography>
                          }
                        >
                          <span>
                            <img
                              src={process.env.PUBLIC_URL + '/upgrade_icon.png'}
                              alt="Upgrade Available"
                              style={{
                                width: '20px',
                                height: '20px',
                                cursor: isUpgradeDisabled ? 'not-allowed' : 'pointer',
                                opacity: isUpgradeDisabled ? 0.5 : 1,
                                marginLeft: '5px'
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isUpgradeDisabled) {
                                  handleUpgradeClick(switchNode, latestVersion.sw_version);
                                }
                              }}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>

                  {/* MODEL */}
                  <TableCell style={{ textAlign: 'center' }}>{switchNode.model}</TableCell>

                  {/* PLATFORM */}
                  <TableCell style={{ textAlign: 'center' }}>{switchNode.platform}</TableCell>

                  {/* STATUS */}
                  {switchNode.status !== 'online' ? (
                    <TableCell style={{ textAlign: 'center', color: 'red' }}>Offline</TableCell>
                  ) : switchNode.sw_status === 10 ? (
                    <TableCell
                      style={{ textAlign: 'center', color: 'red', cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFailedSwitch(switchNode);
                        setErrorDialogOpen(true);
                      }}
                    >
                      Upgrade failed. Click for info
                    </TableCell>
                  ) : isReboot ? (
                    <TableCell
                      style={{ textAlign: 'center', cursor: 'pointer', color: statusColor }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRebootClick(switchNode);
                      }}
                    >
                      <Tooltip
                        placement="top"
                        title={
                          <Typography fontSize="12px">
                            Click here to reboot switch, to finalize the upgrade
                          </Typography>
                        }
                      >
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold' }}>
                          <img
                            src={process.env.PUBLIC_URL + '/reboot_icon.png'}
                            alt="Reboot Icon"
                            style={{ width: '20px', height: '20px', marginRight: '5px' }}
                          />
                          Reboot
                        </div>
                      </Tooltip>
                    </TableCell>
                  ) : (
                    <TableCell style={{ textAlign: 'center', color: statusColor }}>{statusText}</TableCell>
                  )}

                  {/* API ACCESS */}
                  <TableCell
                    style={{
                      textAlign: 'center',
                      color: apiAccess.color,
                      cursor: [5, 6, 10, 11].includes(apiAccess.status) ? 'pointer' : 'default'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDialogOpen(true);
                    }}
                  >
                    {apiAccess.status === 2 && newSwitchLoading ? <CircularProgress size={24} /> : apiAccess.text}
                  </TableCell>

                  {/* OPTIONS */}
                  <TableCell
                    style={{ textAlign: 'center', position: 'relative' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip
                      placement="top"
                      title={
                        switchNode.have_logs ? (
                          <Typography fontSize="12px">Logs are available</Typography>
                        ) : (
                          ''
                        )
                      }
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: switchNode.have_logs ? 'orange' : 'inherit',
                          position: 'relative'
                        }}
                      >
                        <IconButton
                          aria-haspopup="true"
                          onClick={(event) => handleOpenMenu(event, switchNode)}
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          <MoreVertIcon />
                        </IconButton>

                        {switchNode.have_logs ? (
                          <WarningAmberIcon style={{ position: 'absolute', right: '0px' }} />
                        ) : null}
                      </div>
                    </Tooltip>

                    <Popper
                      open={openMenu && selectedSwitchNode?.id === switchNode.id}
                      anchorEl={anchorEl}
                      role={undefined}
                      transition
                      disablePortal
                    >
                      {({ TransitionProps, placement }) => (
                        <Grow
                          {...TransitionProps}
                          style={{
                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
                          }}
                        >
                          <Paper>
                            <ClickAwayListener onClickAway={handleCloseMenu}>
                              <Menu anchorEl={anchorEl} open={openMenu} onClose={handleCloseMenu}>
                                <MenuItem
                                  onClick={() => {
                                    fetchConfigAndDownload(selectedSwitchNode.ip, id, selectedSwitchNode);
                                    handleCloseMenu();
                                  }}
                                  disabled={selectedSwitchNode && selectedSwitchNode.config != 1}
                                >
                                  Download Config
                                </MenuItem>

                                <MenuItem
                                  onClick={() => {
                                    updateApi(switchNode.ip);
                                    handleCloseMenu();
                                  }}
                                  disabled={selectedSwitchNode && selectedSwitchNode.api_access == 1}
                                >
                                  Check API
                                </MenuItem>

                                <MenuItem
                                  onClick={() => {
                                    setUpdatingSwitchIp(switchNode.ip);
                                    UpdateSwInfo(switchNode.ip);
                                    handleCloseMenu();
                                  }}
                                  disabled={selectedSwitchNode && selectedSwitchNode.api_access != 1}
                                >
                                  {loadingUpdateSwInfo && updatingSwitchIp === switchNode.ip ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    'Update Info'
                                  )}
                                </MenuItem>

                                <MenuItem
                                  onClick={() => {
                                    get_loggs(selectedSwitchNode.id);
                                    handleCloseMenu();
                                  }}
                                  disabled={selectedSwitchNode && selectedSwitchNode.have_logs == 0}
                                >
                                  Show Logs
                                  {switchNode.have_logs ? (
                                    <CircleIcon fontSize="small" style={{ marginLeft: '5px', color: 'red' }} />
                                  ) : null}
                                </MenuItem>

                                <MenuItem onClick={handleOpenConfirmDialog}>Delete</MenuItem>
                              </Menu>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </TableCell>
                </TableRow>

                {loadingUpdateSwInfo && updatingSwitchIp === switchNode.ip && (
                  <TableRow>
                    <TableCell colSpan={totalColumns} style={{ padding: 0 }}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={sortedFilteredSwitches.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this item?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              delete_id(selectedSwitchNode.id);
              handleCloseConfirmDialog();
            }}
            color="primary"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SwitchTable;